import { Paper, Group, Title, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useDisclosure } from '@mantine/hooks';
import { IconFileDownload, IconMapPin, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/table-core';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CreateSamplingPlanForm,
  SamplingPlanFormValues,
} from '../../components/forms/create-sampling-plan-form';
import SamplingPlansTable from '../../components/tables/sampling-plans-table';
import { samplingApi } from '../../data-access/apis/sampling';
import { useSamplingQueries } from '../../data-access/queries/sampling';

import { FORM_STATUS, useFiltersStore, useSharedQueries } from '@data-access';
import {
  ActionsMenu,
  ActionsMenuItem,
  Drawer,
  FullMainHeight,
} from '@ui';

export const SamplingPlansPage: FC = () => {
  const { t } = useTranslation();
  const [opened, actions] = useDisclosure(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [computationRequestId, setComputationRequestId] = useState<
    number | null
  >(null);
  const hasDownloadedRef = useRef(false);
  const filters = useFiltersStore.use['samplingPlans']().filters;
  const queryClient = useQueryClient();
  const samplingQueries = useSamplingQueries();
  const sharedQueries = useSharedQueries();

  const createPlan = useMutation({
    mutationFn: samplingApi.plans.create,
    onSuccess: () => {
      actions.close();
      queryClient.invalidateQueries({
        queryKey: samplingQueries.plans.lists(),
      });
    },
  });

  const deleteMany = useMutation({
    mutationFn: samplingApi.plans.deleteMany,
    onSuccess: () => {
      setRowSelection({});
      queryClient.invalidateQueries({
        queryKey: samplingQueries.plans.lists(),
      });
    },
  });

  const exportSynthesis = useMutation({
    mutationFn: samplingApi.plans.exportSynthesis,
    onSuccess: (data) => {
      hasDownloadedRef.current = false;
      setComputationRequestId(data.instance_id);
    },
  });

  const downloadExport = useMutation({
    mutationFn: samplingApi.plans.downloadExport,
  });

  const exportShp = useMutation({
    mutationFn: samplingApi.plans.exportShp,
  });

  const computationRequest = useQuery({
    ...sharedQueries.computationRequest({ id: computationRequestId! }),
    enabled: computationRequestId !== null,
  });

  useEffect(() => {
    if (
      computationRequest.data?.status === FORM_STATUS.FINISHED &&
      !hasDownloadedRef.current &&
      !downloadExport.isPending
    ) {
      hasDownloadedRef.current = true;
      downloadExport.mutate({ id: computationRequest.data.id });
      setComputationRequestId(null);
    } else if (computationRequest.data?.status === FORM_STATUS.ERROR) {
      notifications.show({
        title: t('common.ERROR'),
        message: t('common.error.pageError'),
        color: 'red',
      });
      setComputationRequestId(null);
    }
  }, [computationRequest.data, downloadExport, t]);

  const handleCreatePlan = (values: SamplingPlanFormValues) => {
    createPlan.mutate(values);
  };

  const handleExportSynthesis = () => {
    if (rowSelection.all) {
      exportSynthesis.mutate({
        ids: [],
        all: true,
        selectedFilters: filters,
      });
    } else {
      const ids = Object.keys(rowSelection).map((id) => Number(id));
      exportSynthesis.mutate({
        ids,
      });
    }
  };

  const handleExportShp = () => {
    if (rowSelection.all) {
      exportShp.mutate({
        ids: [],
        all: true,
        selectedFilters: filters,
      });
    } else {
      const ids = Object.keys(rowSelection).map((id) => Number(id));
      exportShp.mutate({ ids });
    }
  };

  const items: ActionsMenuItem[] = [
    {
      type: 'action',
      label: t('sampling.download_summary', {
        defaultValue: 'Télécharger le récapitulatif',
      }),
      icon: IconFileDownload,
      onClick: handleExportSynthesis,
    },
    {
      type: 'action',
      label: t('sampling.export_points_shp', {
        defaultValue: 'Exporter les points (SHP)',
      }),
      icon: IconMapPin,
      onClick: handleExportShp,
    },
    {
      type: 'action',
      label: 'Supprimer',
      color: 'red',
      icon: IconTrash,
      onClick: () => {
        modals.openConfirmModal({
          title: t('sampling.delete_confirm_title', {
            defaultValue: 'Supprimer les plans d\'échantillonnage',
          }),
          content: t('sampling.delete_confirm_message', {
            defaultValue: 'Êtes-vous sûr de vouloir supprimer les plans d\'échantillonnage sélectionnés ?',
          }),
          labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
          onConfirm: () => {
            if (rowSelection.all) {
              deleteMany.mutate({
                ids: [],
                all: true,
                selectedFilters: filters,
              });
            } else {
              const ids = Object.keys(rowSelection).map((id) => Number(id));
              deleteMany.mutate({
                ids,
              });
            }
          },
        });
      },
    },
  ];

  return (
    <>
      <FullMainHeight.Root>
        <FullMainHeight.Header>
          <Paper>
            <Group justify="space-between">
              <Title order={2}>{t('sampling.sampling-plans-title')}</Title>

              <Group>
                <Button onClick={actions.open}>
                  {t('sampling.create-sampling-plan-button')}
                </Button>
                <ActionsMenu items={items}>
                  <ActionsMenu.Button
                    disabled={Object.keys(rowSelection).length === 0}
                    loading={
                      deleteMany.isPending ||
                      exportSynthesis.isPending ||
                      exportShp.isPending ||
                      computationRequest.data?.status === FORM_STATUS.PENDING ||
                      downloadExport.isPending
                    }
                  />
                </ActionsMenu>
              </Group>
            </Group>
          </Paper>
        </FullMainHeight.Header>
        <FullMainHeight.Content>
          <SamplingPlansTable
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
          />
        </FullMainHeight.Content>
      </FullMainHeight.Root>

      <Drawer
        onClose={actions.close}
        opened={opened}
        position="right"
        title={t('sampling.creer-un-plan')}
      >
        <CreateSamplingPlanForm
          onCancel={actions.close}
          onSubmit={handleCreatePlan}
        />
      </Drawer>
    </>
  );
};
